import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ChevronDown } from 'lucide-react';

interface CityFAQProps {
  language: string;
  cityName: string;
  areaName?: string;
}

/**
 * City-level FAQ block with visible accordion + FAQPage JSON-LD.
 *
 * Two purposes:
 *  1. UX — answers buyer questions in-context so users don't bounce to search
 *  2. SEO — emits FAQPage schema for Google's rich-result eligibility on
 *     queries like "how long does flower delivery take in cairo", "minimum
 *     flower order egypt", etc.
 *
 * Why questions are city-aware: Google's E-E-A-T scoring rewards content
 * that demonstrates local knowledge. Hardcoding "Cairo" in every FAQ
 * across all 28 cities would be transparent; interpolating the city name
 * makes each FAQPage entry unique enough that Google indexes them
 * separately. Same for area pages.
 *
 * Why 6 Qs not 12: Google caps FAQ rich-result snippets at ~8 anyway,
 * and longer accordions reduce average scroll depth on mobile. 6 is the
 * sweet spot.
 */
const CityFAQ = ({ language, cityName, areaName }: CityFAQProps) => {
  const isAr = language === 'ar';
  const place = areaName ? `${areaName}, ${cityName}` : cityName;
  const placeAr = areaName ? `${areaName} في ${cityName}` : cityName;
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  const qs = isAr
    ? [
        {
          q: `إزاي بتوصّلوا الورد في ${placeAr}؟`,
          a: `بنوصّل ورد طازة من المحل لباب بيتك في ${placeAr} في نفس اليوم لو طلبت قبل الساعة ٤ بعد الظهر. الطلبات اللي بعد كده بتوصّل تاني يوم. كل باقة بتترتّب يدوي يوم التوصيل، فالورد بيوصّل لسه فايح.`,
        },
        {
          q: 'هل في حد أدنى للطلب؟',
          a: 'الحد الأدنى للطلب ٥٠٠ جنيه مصري. التوصيل مجاني داخل القاهرة الكبرى للطلبات اللي فوق ٧٥٠ جنيه. للطلبات الأصغر بنحسب رسوم توصيل صغيرة حسب المنطقة.',
        },
        {
          q: 'إيه طرق الدفع المتاحة؟',
          a: 'بنقبل دفع بالفيزا والماستر كارد عن طريق بوابة "بيموب" الآمنة، كمان فودافون كاش وإنستا باي. الدفع كاش عند الاستلام متاح في القاهرة والجيزة والإسكندرية فقط.',
        },
        {
          q: `هل بتوصّلوا في نفس اليوم في ${placeAr}؟`,
          a: `أيوة، التوصيل في نفس اليوم متاح في ${placeAr} لو طلبت قبل الساعة ٤ عصر. لطلبات الصباح الباكر، اطلب من الليلة اللي قبلها وحدد وقت التوصيل من ٩ ل١٢ الصبح.`,
        },
        {
          q: 'ممكن أحدد ميعاد توصيل معيّن؟',
          a: 'أيوة، عند ما تكمل الطلب تقدر تختار وقت التوصيل من ضمن نوافذ ٢ ساعة (مثلاً ٩-١١ صبحاً، ١٢-٢ ضهراً، ٤-٦ مساءً). للحجوزات الخاصة زي خطوبة أو ميلاد، حدد الميعاد قبل بـ ٢٤ ساعة على الأقل.',
        },
        {
          q: 'الورد بيفضل عايش قد إيه؟',
          a: 'الورد عندنا بيوصّل طازة في نفس يوم القطف من المُورّد، وبيفضل من ٥ ل٧ أيام لو حافظت عليه: غيّر الميّة كل يومين، قص أطراف السيقان، وابعدها عن الشمس المباشرة والتكييف.',
        },
      ]
    : [
        {
          q: `How fast can you deliver flowers in ${place}?`,
          a: `We deliver fresh flowers to your address in ${place} the same day if you order before 4 PM. Orders placed after 4 PM ship next day. Every bouquet is hand-arranged on the day of delivery so flowers arrive at peak freshness.`,
        },
        {
          q: 'Is there a minimum order amount?',
          a: 'The minimum order is 500 EGP. Delivery is free for orders over 750 EGP in Greater Cairo. Smaller orders include a small delivery fee that varies by area.',
        },
        {
          q: 'What payment methods do you accept?',
          a: 'We accept Visa and Mastercard via the secure Paymob gateway, plus Vodafone Cash and InstaPay. Cash on delivery is available in Cairo, Giza, and Alexandria only.',
        },
        {
          q: `Do you offer same-day delivery in ${place}?`,
          a: `Yes — same-day delivery is available in ${place} for orders placed before 4 PM. For early morning delivery, place your order the night before and pick the 9 AM-12 PM window.`,
        },
        {
          q: 'Can I choose a specific delivery time?',
          a: 'Yes — at checkout you can pick a 2-hour delivery window (e.g. 9-11 AM, 12-2 PM, 4-6 PM). For special occasions like engagements or birthdays, please book at least 24 hours in advance.',
        },
        {
          q: 'How long do the flowers last?',
          a: 'Our flowers arrive fresh on the day they were cut by the supplier and typically last 5-7 days with care: change the water every two days, trim the stems, and keep them away from direct sun and AC.',
        },
      ];

  // FAQPage JSON-LD — emit for SERP rich-result eligibility
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: qs.map((qa) => ({
      '@type': 'Question',
      name: qa.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: qa.a,
      },
    })),
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <section className="py-12 md:py-16 bg-background" aria-labelledby="city-faq-heading">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className={`text-center mb-8 ${isAr ? 'text-right sm:text-center' : ''}`}>
            {/* H2 uses flex+justify-center so it stays visually centered even
                when the wrapper falls back to text-right on Arabic mobile —
                matches the NewArrivals "وصل حديثاً" header pattern exactly. */}
            <h2
              id="city-faq-heading"
              className="text-3xl font-bold text-foreground mb-2 flex items-center justify-center gap-2"
            >
              {isAr ? `الأسئلة الشائعة عن توصيل الورد في ${placeAr}` : `Flower Delivery FAQ — ${place}`}
            </h2>
            <p className="text-muted-foreground text-center">
              {isAr
                ? 'كل اللي محتاج تعرفه عن الطلب والتوصيل والدفع'
                : 'Everything you need to know about ordering, delivery, and payment'}
            </p>
          </div>

          <ul className="space-y-2" dir={isAr ? 'rtl' : 'ltr'}>
            {qs.map((qa, idx) => {
              const isOpen = openIdx === idx;
              return (
                <li key={idx} className="border border-border rounded-lg overflow-hidden bg-card">
                  <button
                    type="button"
                    onClick={() => setOpenIdx(isOpen ? null : idx)}
                    aria-expanded={isOpen}
                    className="w-full px-4 md:px-5 py-3.5 flex items-center justify-between gap-3 text-left hover:bg-muted/40 transition-colors"
                  >
                    <h3 className="font-semibold text-base text-foreground flex-1">{qa.q}</h3>
                    <ChevronDown
                      className={`h-4 w-4 text-muted-foreground flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      aria-hidden="true"
                    />
                  </button>
                  {isOpen && (
                    <div className="px-4 md:px-5 pb-4 pt-1 text-sm text-muted-foreground leading-relaxed">
                      {qa.a}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </section>
    </>
  );
};

export default CityFAQ;
